import React from "react";
import { motion } from "framer-motion";
import OurCoreValues from "./OurCoreValues";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faGlobe, 
  faHandshake, 
  faGraduationCap, 
  faIndustry 
} from "@fortawesome/free-solid-svg-icons";

interface CorporateSection {
  title: string;
  text: string;
  tag: string;
  icon: typeof faGlobe;
}

const corporateSections: CorporateSection[] = [
  {
    title: "Pan-India & Global Supply Chain",
    tag: "Scale & Logistics",
    text: "Wardha Mushroom is an agro-industrial leader specializing in the high-volume bulk supply of premium Fresh Button Mushrooms and Dried Oyster Mushrooms. From our state-of-the-art facilities, we manage robust supply networks feeding both major domestic commercial markets and strict international export trade corridors.",
    icon: faGlobe,
  },
  {
    title: "Turnkey Contract Farming Setup",
    tag: "Infrastructure",
    text: "We mastermind end-to-end commercial contract cultivation networks across Maharashtra. Our team delivers absolute turnkey integration—from engineering and laboratory environment setup to providing elite-grade laboratory spawn, culminating in guaranteed institutional buyback contracts.",
    icon: faIndustry,
  },
  {
    title: "Advanced Agritech Training Network",
    tag: "Empowerment",
    text: "We are committed to securing the supply chains of tomorrow. By training more than 1,000+ students and aspiring entrepreneurs across Maharashtra, we instill rigorous technical expertise, production safety standards, and modern commercial cultivation tactics into our expanding grower network.",
    icon: faGraduationCap,
  },
  {
    title: "Strategic Industrial Mission",
    tag: "Our Purpose",
    text: "To optimize agricultural supply chains through precision agritech innovations, empowering our regional farming ecosystem while guaranteeing global enterprises access to pristine, uninterrupted, and ethically cultivated mushroom bioproducts.",
    icon: faHandshake,
  },
];

const AboutUs: React.FC = () => {
  return (
    <div className="bg-neutral-50 min-h-screen text-neutral-900 overflow-hidden">
      
      {/* High-End Enterprise Hero Banner */}
      <div 
        className="relative h-[450px] flex items-center justify-center bg-cover bg-center"
        style={{ 
          backgroundImage: "url('/images/Aboutusnew.webp')", 
          backgroundAttachment: 'fixed' 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/90 via-neutral-900/80 to-neutral-950"></div>
        
        <div className="relative text-center z-10 px-6 max-w-4xl mx-auto">
          <motion.span 
            className="text-xs uppercase font-mono tracking-widest text-emerald-400 bg-emerald-950/60 px-4 py-1.5 rounded-full border border-emerald-800/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Institutional Profile
          </motion.span>
          
          <motion.h1 
            className="text-4xl md:text-6xl font-bold font-serif text-white mt-4 mb-4 tracking-tight"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Corporate Overview
          </motion.h1>
          
          <motion.p 
            className="text-base md:text-xl text-neutral-300 font-light max-w-2xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Powering domestic pharmaceutical, food processing, culinary hospitality markets, and global trade corridors.
          </motion.p>
        </div>
      </div>

      {/* Grid Corporate Infrastructure Layout */}
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {corporateSections.map((section, index) => (
            <motion.article 
              className="bg-white border border-neutral-200/70 p-8 rounded-2xl shadow-sm hover:shadow-xl hover:border-emerald-500/30 transition-all duration-300 flex flex-col md:flex-row items-start gap-6 group"
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: index * 0.1, ease: "easeOut" }}
            >
              {/* Premium Icon Container */}
              <div className="w-14 h-14 shrink-0 rounded-xl bg-neutral-50 border border-neutral-100 flex items-center justify-center text-emerald-700 group-hover:bg-emerald-950 group-hover:text-emerald-400 transition-all duration-300 shadow-sm">
                <FontAwesomeIcon icon={section.icon} className="text-xl" />
              </div>

              {/* Text Matrix */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono font-bold tracking-wider text-emerald-700 uppercase bg-emerald-50 px-2 py-0.5 rounded">
                    {section.tag}
                  </span>
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-neutral-900 tracking-wide">
                  {section.title}
                </h2>
                <p className="text-neutral-600 text-sm md:text-base leading-relaxed text-justify">
                  {section.text}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Core Values Corporate Segment */}
      <div className="border-t border-neutral-200 bg-white">
        <OurCoreValues />
      </div>
    </div>
  );
};

export default AboutUs;